import {
  type AppVersionByPlatform,
  type AppVersionConfig,
  type AppVersionData,
} from "@fitness-app/data-models/entities/TrainerConfig";
import { type MobileUpdateType } from "@fitness-app/data-models/entities/AppRelease";

type Platform = keyof AppVersionByPlatform;

type PlatformFormValue = {
  current: string;
  minRequired: string;
  versionsWithIssues: string;
  updateType?: MobileUpdateType | null;
};

export type AppConfigFormValue = Record<Platform, PlatformFormValue>;

export const platforms: { key: Platform; label: string }[] = [
  { key: "ios", label: "iOS" },
  { key: "android", label: "Android" },
  { key: "web", label: "Web" },
  { key: "admin", label: "Panel admina" },
];

export const isMobilePlatform = (platform: Platform) => platform === "ios" || platform === "android";

export const mapConfigToForm = (config: AppVersionConfig): AppConfigFormValue =>
  Object.fromEntries(
    platforms.map(({ key }) => [
      key,
      {
        current: config.data[key]?.current ?? "",
        minRequired: config.data[key]?.minRequired ?? "",
        versionsWithIssues: config.data[key]?.versionsWithIssues?.join("\n") ?? "",
        updateType: config.data[key]?.updateType ?? null,
      },
    ]),
  ) as AppConfigFormValue;

export function updateTypeForVersion(current: string, previous: AppVersionData | null | undefined) {
  return current.trim() === previous?.current.trim() ? (previous.updateType ?? null) : null;
}

export function mapFormToConfig(values: AppConfigFormValue, previous: AppVersionByPlatform) {
  const data = { ...previous };
  for (const { key } of platforms) {
    const value = values[key];
    const current = value.current.trim();
    if (isMobilePlatform(key) && current !== previous[key]?.current.trim() && !value.updateType) {
      throw new Error("Wybierz typ aktualizacji dla nowej wersji aplikacji.");
    }
    data[key] = {
      ...previous[key],
      current,
      minRequired: value.minRequired.trim(),
      versionsWithIssues: value.versionsWithIssues
        .split("\n")
        .map((version) => version.trim())
        .filter(Boolean),
      ...(isMobilePlatform(key)
        ? { updateType: value.updateType ?? updateTypeForVersion(current, previous[key]) }
        : {}),
    };
  }
  return { data };
}
